<div *ngIf="purchaseOrder$ | async; let po">
  <div class="w-full px-3 title text-white">
    <div class="flex flex-row justify-between">
      <div class="flex flex-row gap-2">
        <h2 class="px-0">
          {{ po.name }}
          <span *ngIf="po.closed">
            (Closed)
          </span>
        </h2>
        <mat-icon
          matTooltip="PO Paused"
          class="text-red-501 pauseIcon m-0"
          *ngIf="po.pausedTimestamp !== null && !po.closed"
        >
          pause
        </mat-icon>
      </div>
      <div *ngIf="!po.closed" class="flex flex-row gap-2 items-center">
        <ng-container *saAccountType="['lmo']">
          <ng-container *saAllowedAccountRoles="['admin', 'dispatcher']">
            <button
              *ngIf="po.pausedTimestamp === null; else unpauseButton"
              mat-flat-button
              color="primary"
              (click)="promptPause()"
            >
              PAUSE
            </button>
            <ng-template #unpauseButton>
              <button mat-flat-button color="primary" (click)="unpausePO()">
                UNPAUSE
              </button>
            </ng-template>
            <sa-network-active-button [active]="activeNetworkRequest$$ | async" *ngIf="!po.closed">
              <button mat-flat-button color="warn" (click)="confirmClosePO(po.id)">CLOSE</button>
            </sa-network-active-button>
          </ng-container>
        </ng-container>
      </div>
    </div>
  </div>

  <div class="px-3 pb-3">
    <a class="flex flex-row py-2 px-1 w-fit" [routerLink]="['/', 'lmo', 'purchase_order', 'list']">
      <i class="material-icons">arrow_back</i>
      Back to List
    </a>

    <div class="flex flex-row items-baseline gap-2">
      <h3>
        {{ po.mineName }}
      </h3>
      <ng-container *saAllowedAccountRoles="['admin', 'dispatcher']">
        <ng-container *saFeatureFlag="'loaderAnalyticsV2'">
          <a [routerLink]="['/', 'loader-analytics-dashboard-v2', 'purchase-order', po.id]">View Loader Integration</a>
        </ng-container>
      </ng-container>
      |
      <a (click)="viewTimeline()">View Timeline</a>
    </div>
    <div class="text-sm text-gray-700">{{ po.meshType }} Mesh</div>

    <div class="w-full grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-3 pt-2">
      <div class="flex flex-col">
        <div>Total</div>
        <mat-progress-bar
          [color]="(po.totalAllocationUsed / po.allocation) * 100 > 100 ? 'warn' : 'primary'"
          mode="determinate"
          [value]="(po.totalAllocationUsed / po.allocation) * 100"
          class="my-1 usage-bar"
        ></mat-progress-bar>
        <div>
          {{ po.totalAllocationUsed || 0 | poLoaderPoundTonConversion | approxBigNumber }} of
          {{ po.allocation || 0 | poLoaderPoundTonConversion | approxBigNumber | poLoaderPoundTonUnit }}
        </div>
      </div>
      <div *ngIf="!po.closed" class="flex flex-col">
        <div>Today</div>
        <mat-progress-bar
          [color]="(po.orderedToday / po.maximumDailyAllocation) * 100 > 100 ? 'warn' : 'primary'"
          mode="determinate"
          [value]="(po.orderedToday / po.maximumDailyAllocation) * 100"
          class="my-1 usage-bar"
        ></mat-progress-bar>
        <div>
          {{ po.orderedToday || 0 | poLoaderPoundTonConversion | approxBigNumber }} of
          {{ po.maximumDailyAllocation || 0 | poLoaderPoundTonConversion | approxBigNumber | poLoaderPoundTonUnit }}
          <span *ngIf="po.minimumDailyAllocation"
            >({{
              po.minimumDailyAllocation || 0 | poLoaderPoundTonConversion | approxBigNumber | poLoaderPoundTonUnit
            }}
            Minimum Per Day)</span
          >
        </div>
      </div>
    </div>

    <div class="w-ful grid grid-cols-1 lg:grid-cols-7 gap-x-4 gap-y-3 pt-2">
      <div class="flex flex-col lg:col-span-3">
        <div class="w-full flex flex-row justify-between panel-header items-center">
          <h3>
            Details
          </h3>
          <ng-container *saAccountType="['lmo']">
            <ng-container *saAllowedAccountRoles="['admin', 'dispatcher']">
              <button
                *ngIf="!po.closed"
                mat-flat-button
                color="primary"
                class="self-center"
                [disabled]="!(purchaseOrderFormDirty$$ | async)"
                (click)="updatePO()"
              >
                SAVE
              </button>
            </ng-container>
          </ng-container>
        </div>
        <form *ngIf="purchaseOrderForm" [formGroup]="purchaseOrderForm">
          <div class="flex flex-col py-2">
            <div class="flex flex-col">
              <div class="form-field-label">
                <label>Total Tonnage</label>
              </div>
              <mat-form-field appearance="outline">
                <input formControlName="totalTonnage" matInput sa-number-input />
              </mat-form-field>
            </div>

            <div class="flex flex-col">
              <div class="form-field-label">
                <label>Daily Minimum Tonnage</label>
              </div>
              <mat-form-field appearance="outline">
                <input formControlName="dailyMinimumTonnage" matInput sa-number-input />
              </mat-form-field>
            </div>

            <div class="flex flex-col">
              <div class="form-field-label">
                <label>Daily Maximum Tonnage</label>
              </div>
              <mat-form-field appearance="outline">
                <input formControlName="dailyMaximumTonnage" matInput sa-number-input />
              </mat-form-field>
            </div>

            <div class="flex flex-col">
              <div class="form-field-label">
                <label>Cost Per Ton</label>
              </div>
              <mat-form-field appearance="outline">
                <input formControlName="costPerTon" matInput autocomplete="off" saCurrencyInput />
              </mat-form-field>
            </div>

            <div class="flex flex-col">
              <div class="form-field-label">
                <label>Daily Allocation Reset ({{ timezoneShortName }})</label>
              </div>
              <mat-form-field appearance="outline">
                <input
                  matInput
                  class="datepicker-input"
                  autocomplete="off"
                  formControlName="dailyAllocationReset"
                  [owlDateTime]="resetAllocationTime"
                  [owlDateTimeTrigger]="resetAllocationTime"
                />
                <owl-date-time #resetAllocationTime [pickerType]="'timer'"></owl-date-time>
              </mat-form-field>
            </div>

            <div class="flex flex-col" *saFeatureFlag="'purchaseOrderAllocationNotifications'">
              <div class="form-field-label">
                <label>Subscribed Users</label>
              </div>
              <mat-form-field appearance="outline">
                <mat-select
                  formControlName="subscribedSandiUsers"
                  multiple
                  saAutomatedSelector="well-creation__subscribed-users"
                >
                  <mat-option>
                    <ngx-mat-select-search
                      [placeholderLabel]="'Subscribed Users'"
                      [noEntriesFoundLabel]="'No User found'"
                      [formControl]="filterUser"
                    ></ngx-mat-select-search>
                  </mat-option>
                  <mat-option
                    *ngFor="let user of filteredUsers$ | async; trackBy: filteredUsers$ | trackByKey: 'id'"
                    [value]="user.id"
                    (click)="purchaseOrderFormDirty$$.next(true)"
                  >
                    <span>{{ user.name }}</span>
                  </mat-option>
                </mat-select>
                <mat-hint
                  >Select which users will receive notifications when this PO is nearing or over capacity.
                </mat-hint>
              </mat-form-field>

              <div *ngIf="isGroupedAccountUser$ | async" class="flex flex-col" style="margin-top: 24px;">
                <div class="form-field-label">
                  <label>Permitted Fracs (leave blank for all)</label>
                </div>
                <!--                <mat-label>Permitted Fracs (leave blank for all)</mat-label>-->
                <mat-form-field appearance="outline">
                  <mat-select formControlName="permittedFracIds" multiple>
                    <mat-option
                      *ngFor="let frac of fracList$ | async; trackBy: fracList$ | async | trackByKey: 'id'"
                      [value]="frac.lmoFracSummary.id"
                      (click)="purchaseOrderFormDirty$$.next(true)"
                      >{{ frac.lmoFracSummary.name }}</mat-option
                    >
                  </mat-select>
                </mat-form-field>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div class="flex flex-col lg:col-span-2">
        <div class="w-full flex flex-row justify-between panel-header items-center">
          <h3>Attachments</h3>
          <ng-container *saAccountType="['lmo']">
            <ng-container *saAllowedAccountRoles="['admin', 'dispatcher']">
              <label #fileUpload>
                <input
                  class="hidden"
                  [disabled]="uploadingPOAttachment$$.asObservable() | async"
                  (change)="onFileChange($event)"
                  type="file"
                  accept="application/pdf, image/*"
                />
                <button mat-flat-button color="primary" (click)="onUploadClick($event)" *ngIf="!po.closed">
                  <span *ngIf="!(uploadingPOAttachment$$.asObservable() | async)">UPLOAD</span>
                  <span *ngIf="uploadingPOAttachment$$.asObservable() | async">Uploading...</span>
                </button>
              </label>
            </ng-container>
          </ng-container>
        </div>
        <div *ngIf="!po.uploadedFiles?.length" class="text-gray-700 pt-2">
          There are currently no attachments
        </div>
        <div *ngIf="po.uploadedFiles?.length > 0" class="flex flex-col p-2 gap-2">
          <div
            *ngFor="let file of po.uploadedFiles; trackBy: po.uploadedFiles | trackByKey: 'id'"
            class="w-full grid grid-cols-3 image-row"
          >
            <div class="image-container">
              <div class="image-preview">
                <img [src]="file.url" *ngIf="isImage(file.fileName); else noPreview" (click)="openInWindow(file.url)" />
                <ng-template #noPreview>
                  <img src="../../../../../assets/icons/pdf.svg" (click)="openInWindow(file.url)" />
                </ng-template>
              </div>
            </div>
            <div class="flex flex-col col-span-2 pl-2 pb-2">
              <div class="flex flex-row-reverse w-full">
                <button mat-icon-button [matMenuTriggerFor]="attachmentMenu" (click)="$event.stopPropagation()">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #attachmentMenu="matMenu">
                  <button mat-menu-item (click)="openInWindow(file.url)">
                    View
                  </button>
                  <button mat-menu-item (click)="deleteAttachment(file.id, po.id)" *ngIf="!po.closed">
                    Delete
                  </button>
                </mat-menu>
              </div>
              <h3>
                {{ file.fileName }}
              </h3>
              <div class="text-gray-700">
                Uploaded on {{ file.uploadedTimestamp | standardDate }} by {{ file.uploadedBy }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col lg:col-span-2">
        <div class="w-full flex flex-row justify-between panel-header items-center">
          <h3>Completed Orders ({{ po.orders?.length || 0 }})</h3>
        </div>
        <div class="overflow-y-hidden orders-container">
          <div *ngIf="!po.orders?.length" class="text-gray-700 pt-2">
            There are currently no completed orders
          </div>
          <div *ngFor="let order of po.orders; trackBy: po.orders | trackByKey: 'id'" class="pb-1 order">
            <h3>
              {{ order.id | orderId }}
            </h3>
            <a [routerLink]="['/', 'lmo', 'frac', order.fracId, 'home']">
              {{ order.fracName }}
            </a>
            <div>
              {{ order.vendorName }}
            </div>
            <div class="text-gray-700">Completed at {{ order.completedAt | standardDate }}</div>
            <a [routerLink]="['/', 'invoicing', 'order', order.id]">
              Open in Billing Portal
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col w-full">
      <div class="w-full panel-header">
        <h2>
          Wells
        </h2>
      </div>

      <div class="w-full pt-3">
        <h3 class="flex items-center">
          <div class="mr-2">Supported Wells</div>
          <button mat-flat-button color="primary" (click)="promptNewSupportedWell()">
            Add New
          </button>
        </h3>
        <div *ngIf="po.currentFracs?.length == 0" class="text-gray-700">
          There are currently no supported wells
        </div>
        <div *ngIf="fcpoFormsInitialized$$ | async" class="w-full flex flex-col gap-2">
          <div
            *ngFor="let frac of po.currentFracs; let i = index; trackBy: po.currentFracs | trackByKey: 'id'"
            class="flex flex-row w-full gap-2 current-frac-form pb-2"
            [formGroup]="fcpoForms[i]"
          >
            <div class="flex flex-col flex-1 well-info">
              <h3>
                <a [routerLink]="['/', 'lmo', 'frac', frac.id, 'home']">
                  {{ frac.fracName }}
                </a>
              </h3>
              <div *ngIf="frac.currentStage > 1">
                <div
                  *ngIf="
                    frac.minutesUntilNPTNoPending === undefined ||
                      frac.minutesUntilNPTNoPending === null ||
                      frac.minutesUntilNPTNoPending > 840;
                    else hasNPT
                  "
                  class="good-npt"
                >
                  14h+ to NPT
                </div>
                <ng-template #hasNPT>
                  <div [class.warning]="frac.minutesUntilNPTNoPending <= 300">
                    {{ frac.minutesUntilNPTNoPending | minutesToHuman }} until NPT (Ex. Pending Orders)
                  </div>
                </ng-template>
              </div>
              <div *ngIf="frac.currentStage > 1">Stage {{ frac.currentStage - 1 }} of {{ frac.totalStages }}</div>
              <div *ngIf="frac.currentStage <= 1">
                Prefill
              </div>
              <div *ngIf="frac.previousStageCompletedTimestamp" class="text-gray-700">
                Last stage completed at {{ frac.previousStageCompletedTimestamp | standardDate }}
              </div>
            </div>
            <div class="flex-1">
              <div class="flex flex-col w-full">
                <div class="form-field-label">
                  <label>Billable Mileage</label>
                </div>
                <mat-form-field appearance="outline">
                  <input matInput formControlName="billableMileage" type="number" min="0" />
                </mat-form-field>
                <mat-error *ngIf="fcpoForms[i].controls['billableMileage'].hasError('required')">
                  Billable Mileage is required
                </mat-error>
              </div>
            </div>
            <div class="flex-1">
              <div class="flex flex-col w-full">
                <div class="form-field-label">
                  <label>Purchase Order Line Item Number</label>
                </div>
                <mat-form-field appearance="outline">
                  <input matInput formControlName="poLineItemNumber" autocomplete="off" type="text" min="0" />
                </mat-form-field>
                <mat-error *ngIf="fcpoForms[i].controls['poLineItemNumber'].hasError('required')">
                  Line Item Number is required
                </mat-error>
              </div>
            </div>
            <div class="flex-1" *saFeatureFlag="'defaultLoadWeightInput'">
              <div class="flex flex-col w-full">
                <div class="form-field-label">
                  <label>Default Load Weight</label>
                </div>
                <mat-form-field appearance="outline">
                  <input matInput formControlName="defaultLoadWeight" autocomplete="off" sa-number-input />
                  <mat-hint
                    >Most of the time, this should be left blank.
                    <mat-icon
                      matTooltip="This is used when city/state/county regulations require running lighter loads (due to frost laws, spring melt, etc.)"
                      >info</mat-icon
                    ></mat-hint
                  >
                </mat-form-field>
              </div>
            </div>
            <ng-container *saAccountType="['lmo']">
              <ng-container *saAllowedAccountRoles="['admin', 'dispatcher']">
                <div class="fcpo-save-button-container self-auto" *ngIf="!po.closed">
                  <input formControlName="percentBasedAutoOrderingAllocation" type="hidden" />
                  <button
                    *ngIf="!po.closed"
                    mat-flat-button
                    color="primary"
                    [disabled]="!(fcpoFormsDirty$$[i] | async) || !(fcpoFormsValid$$[i] | async)"
                    (click)="updateFcpo(i)"
                  >
                    SAVE
                  </button>
                </div>
              </ng-container>
            </ng-container>
          </div>
        </div>
      </div>

      <div class="w-full pt-3">
        <h3>
          Permitted Wells
        </h3>
        <div *ngIf="(po.permittedFracs?.length || 0) === 0" class="text-gray-700">
          Available to all wells
        </div>
        <div *ngIf="po.permittedFracs?.length > 0" class="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          <div
            *ngFor="let frac of po.permittedFracs; let i = index; trackBy: po.permittedFracs | trackByKey: 'id'"
            class="flex flex-row w-full items-baseline gap-2 current-frac-form pb-2"
          >
            <div class="flex flex-col">
              <h3>
                <a [routerLink]="['/', 'lmo', 'frac', frac.id, 'home']">
                  {{ frac.fracName }}
                </a>
              </h3>
              <div *ngIf="frac.currentStage > 1">
                <div
                  *ngIf="
                    frac.minutesUntilNPTNoPending === undefined ||
                      frac.minutesUntilNPTNoPending === null ||
                      frac.minutesUntilNPTNoPending > 840;
                    else hasNPT
                  "
                >
                  14h+ to NPT
                </div>
                <ng-template #hasNPT>
                  <div>{{ frac.minutesUntilNPTNoPending | minutesToHuman }} until NPT (Ex. Pending Orders)</div>
                </ng-template>
              </div>
              <div *ngIf="frac.currentStage > 1">Stage {{ frac.currentStage - 1 }} of {{ frac.totalStages }}</div>
              <div *ngIf="frac.currentStage <= 1">
                Prefill
              </div>
              <div *ngIf="frac.previousStageCompletedTimestamp" class="text-gray-700">
                Last stage completed at {{ frac.previousStageCompletedTimestamp | standardDate }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<ng-template #defaultLoadWeightWarning>
  <div mat-dialog-title>
    Confirm Default Load Weight
  </div>
  <div mat-dialog-content>
    You have entered a default load weight
    <span class="font-semibold">({{ defaultLoadWeight | number }} lbs)</span> which is less than half of the expected
    load weight, are you sure you wish to proceed?
  </div>
  <div mat-dialog-actions class="flex justify-between">
    <button mat-flat-button [matDialogClose]="false">GO BACK</button>
    <button mat-flat-button color="warn" [matDialogClose]="true">SAVE ANYWAY</button>
  </div>
</ng-template>

<ng-template #billableMileageWarningModal>
  <div mat-dialog-title>
    Do you want the Billable Mileage updated to {{ billableMileage | number }} on the other POs out of this loader?
  </div>
  <div mat-dialog-actions class="flex justify-between">
    <button mat-flat-button [matDialogClose]="false">GO BACK</button>
    <button mat-flat-button color="warn" [matDialogClose]="true">CONFIRM</button>
  </div>
</ng-template>

<ng-template #pauseDescription>
  <div mat-dialog-title>
    Pause Reason
  </div>
  <div mat-dialog-content>
    <div class="flex flex-col w-full">
      <div>Please enter reason for pause</div>
      <div class="w-full">
        <mat-form-field class="w-full">
          <mat-label>Pause Reason</mat-label>
          <textarea matInput [formControl]="pauseReason"></textarea>
        </mat-form-field>
      </div>
    </div>
  </div>
  <div mat-dialog-actions class="flex justify-between">
    <button mat-flat-button mat-dialog-close>CANCEL</button>
    <button mat-flat-button color="warn" (click)="pausePO()" mat-dialog-close>PAUSE PO</button>
  </div>
</ng-template>

<ng-template #confirmClose>
  <h1 mat-dialog-title>Close Purchase Order</h1>
  <div mat-dialog-content>
    <p>Are you sure you want to close this purchase order?</p>
  </div>
  <div mat-dialog-actions class="actions">
    <button mat-flat-button [mat-dialog-close]="false" color="primary">CANCEL</button>
    <button mat-flat-button [mat-dialog-close]="true" color="warn">CLOSE</button>
  </div>
</ng-template>
